import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Medialink } from '../../api/medialink';
import { Module } from '../../api/module';
import { NodeWithoutDescription } from '../../api/table-of-content';
import {
  deleteUserMaterial,
  postUserMaterialBundle,
  UserMaterial,
  UserMaterialBundleMember,
} from '../../api/user-material';
import { TOAST_ID } from '../../constants/ToastIds';
import ModalContext from '../../contexts/ModalContext';
import { useDefaultToast } from '../../hooks/useDefaultToast';
import useLicenses from '../../hooks/useLicenses';
import { useRoutes } from '../../hooks/useRoutes';
import useMedialinks from '../../pages/material/toc/hooks/medialink/useMedialinks';
import useUserMaterial from '../../pages/material/toc/hooks/user-material/useUserMaterial';
import { buildGroupProps } from '../../utils/elementisUtils';
import { getDescriptionOfNode } from '../../utils/getNodeDescription';
import ConfirmationModal from '../ConfirmationModal';
import TOCLevelSelectorModal from '../TOCLevelSelectorModal';
import { isMedialink, Material } from './material-list/materialUtils';
import { computeBundleMaxScore } from './utils';

import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import TextArea from '@pelckmans/elementis/components/text-area';
import TextField from '@pelckmans/elementis/components/text-field';
import { useToast } from '@pelckmans/elementis/components/toast';
import MaterialList from './material-list/MaterialList';

interface Props {
  id: string;
  node?: NodeWithoutDescription;
  module: Module;
  superModule: Module;
  subModules: Module[];
  onCreate: () => void;
  studioGroup: StudioGroup;
  initialMaterials?: UserMaterialBundleMember[];
}

interface FormValues {
  name: string;
  instruction?: string;
  materials: UserMaterialBundleMember[];
  nodeId: string;
}

const ID = 'toc-level-selector-modal';
const CANCEL_CONFIRMATION_ID = 'cancel-bundle-confirmation';

// TODO: use BaseAddMaterialModal if possible?
export default function BundleModal({
  id,
  node,
  module,
  superModule,
  subModules,
  onCreate,
  studioGroup,
  initialMaterials,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { closeModal, showModal } = useContext(ModalContext);
  const { isModuleLicensed } = useLicenses();
  const { openMedialink } = useMedialinks();
  const { openMaterial } = useUserMaterial();
  const accessibleSubModules = subModules.filter(
    m => isModuleLicensed(m) && m.publishingState !== 'preview' && m.publishingState !== 'review',
  );
  const { failedToast, successToast } = useDefaultToast();
  const { addToast } = useToast();
  const { buildAbsolutePath } = useRoutes();
  const [showAlert, setShowAlert] = useState(false);
  const [materials, setMaterials] = useState<UserMaterialBundleMember[]>(initialMaterials ?? []);
  const [items, setItems] = useState<(UserMaterial | Medialink)[]>([]);
  const [selectedNode, setSelectedNode] = useState<NodeWithoutDescription | null>(node ?? null);
  const [selectedModule, setSelectedModule] = useState<Module>(module);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: { nodeId: node?.id },
  });

  const handleMaterialAdded = useCallback((userMaterial: UserMaterial) => {
    setMaterials(prev => [...prev, { materialId: userMaterial.id, materialType: 'user-material' }]);
  }, []);

  const handleMaterialDeleted = useCallback(
    async (material: Material) => {
      if (!isMedialink(material)) {
        try {
          await deleteUserMaterial(material.moduleId, material.node, material.id);
        } catch (error) {
          failedToast();
          return;
        }
      }

      const materialType = isMedialink(material) ? 'medialink' : 'user-material';
      setMaterials(prev => prev.filter(m => !(m.materialId === material.id && m.materialType === materialType)));
    },
    [failedToast],
  );

  const handleMaterialOpened = useCallback(
    async (material: Material) => {
      if (isMedialink(material)) {
        if (!selectedNode) return;

        const modal = await openMedialink(material, selectedNode, selectedModule);
        if (modal) showModal(modal);
        return;
      }

      const modal = await openMaterial(material, selectedNode ? [selectedNode] : []);
      if (modal) showModal(modal);
    },
    [selectedNode, selectedModule, openMedialink, openMaterial, showModal],
  );

  async function handleOnConfirm() {
    closeModal(id);
  }

  function handleClose() {
    const name = getValues('name').trim();

    showModal(
      <ConfirmationModal
        id={CANCEL_CONFIRMATION_ID}
        headerTitle={
          name
            ? t('SHARED_COMPONENTS.ADD_BUNDLE.CANCEL_CONFIRMATION.TITLE', { name })
            : t('SHARED_COMPONENTS.ADD_BUNDLE.CANCEL_CONFIRMATION.TITLE_EMPTY')
        }
        title={t('SHARED_COMPONENTS.ADD_BUNDLE.CANCEL_CONFIRMATION.BODY')}
        message={t('SHARED_COMPONENTS.ADD_BUNDLE.CANCEL_CONFIRMATION.SUBTEXT')}
        confirmationText={t('SHARED_COMPONENTS.ADD_BUNDLE.CANCEL_CONFIRMATION.ACTIONS.CONFIRM')}
        onConfirm={handleOnConfirm}
      />,
    );
  }

  async function onSubmit(data: FormValues) {
    if (!selectedNode) return;

    if (materials.length === 0) {
      setShowAlert(true);
      return;
    }

    const { nodeId, name, instruction } = data;

    try {
      await postUserMaterialBundle(selectedModule.id, nodeId, {
        name,
        maxScore: computeBundleMaxScore(items),
        bundle: { instruction, materials },
      });

      const isCurrentLocation = node && node.id === selectedNode.id && module.id === selectedModule.id;

      if (isCurrentLocation) {
        successToast();
      } else {
        const tocLink = buildAbsolutePath(['MATERIAL', 'MATERIAL.CONTENT', 'MATERIAL.CONTENT.TOC'], {
          subjectSlug: superModule.subjectSlug,
          methodSlug: superModule.methodSlug,
          moduleSlug: superModule.slug,
          selectedModuleSlug: selectedModule.slug,
          nodeId: selectedNode.id,
        });

        addToast({
          id: TOAST_ID.ADD_BUNDLE_SUCCESS_NOT_VISIBLE,
          variant: 'success',
          title: t('TOAST.SUCCESS.TITLE'),
          children: t('SHARED_COMPONENTS.ADD_BUNDLE.TOASTS.SUCCESS_NOT_VISIBLE.MESSAGE'),
          actions: buildGroupProps(
            <Button prominence="neutral" as="link" href={tocLink}>
              {t('SHARED_COMPONENTS.ADD_BUNDLE.TOASTS.SUCCESS_NOT_VISIBLE.ACTION')}
            </Button>,
          ),
        });
      }

      onCreate();
      closeModal(id, true);
    } catch (error) {
      failedToast();
    }
  }

  function handleOpenNodeSelector() {
    showModal(
      <TOCLevelSelectorModal
        id={ID}
        superModule={superModule}
        subModules={accessibleSubModules}
        initialSubModule={selectedModule}
        onSelect={(node, moduleId) => {
          setSelectedNode(node);
          setSelectedModule(accessibleSubModules.find(m => m.id === moduleId)!);
          setValue('nodeId', node.id, { shouldValidate: isSubmitted });
        }}
        selectedNode={selectedNode}
      />,
    );
  }

  useEffect(() => {
    if (isSubmitted && errors && Object.values(errors).some(error => error?.message)) {
      setShowAlert(true);
    }
  }, [isSubmitted, errors]);

  const canShare = studioGroup === 'BAO' ? superModule.licenses.hasBaollLicense : true;

  return (
    <Dialog id={id} aria-label={t('SHARED_COMPONENTS.ADD_BUNDLE.A11Y_LABEL')} modal size="max">
      <Dialog.Header
        title={t('SHARED_COMPONENTS.ADD_BUNDLE.TITLE')}
        onClose={handleClose}
        aria-label={t('SHARED_COMPONENTS.ADD_BUNDLE.CLOSE_LABEL')}
      />
      <Dialog.Body
        alertGroupProps={
          showAlert ? buildGroupProps(<Alert prominence="danger">{t('FORM.ERROR.INVALID')}</Alert>) : undefined
        }
      >
        <form id="form-add-bundle" className="add-bundle-modal-content" onSubmit={handleSubmit(onSubmit)}>
          <div className="content content-secondary">
            <div className="modal-content-form">
              <input type="hidden" {...register('nodeId', { required: t('FORM.VALIDATION.REQUIRED') })} />
              <TextField
                id="name"
                labelText={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.NAME.LABEL')}
                required
                placeholder={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.NAME.PLACEHOLDER')}
                error={!!errors.name}
                errorText={errors.name?.message}
                {...register('name', { required: t('FORM.VALIDATION.REQUIRED') })}
              />
              <TextField
                id="linked-section"
                labelText={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.LINKED_SECTION.LABEL')}
                required
                placeholder={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.LINKED_SECTION.PLACEHOLDER')}
                helpText={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.LINKED_SECTION.HELP_TEXT')}
                disabled
                value={selectedNode ? getDescriptionOfNode(selectedNode, t) : ''}
                error={!!errors.nodeId}
                errorText={errors.nodeId?.message}
                buttonGroupProps={buildGroupProps(
                  <Button prominence="neutral" size="sm" onClick={handleOpenNodeSelector} type="button">
                    {t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.LINKED_SECTION.SELECT_ACTION')}
                  </Button>,
                )}
              />
              <TextArea
                id="instruction"
                richText={true}
                language={language}
                labelText={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.INSTRUCTION.LABEL')}
                placeholder={t('SHARED_COMPONENTS.ADD_BUNDLE.FORM.INSTRUCTION.PLACEHOLDER')}
                {...register('instruction')}
              />
            </div>
          </div>
          <div className="content content-primary">
            <MaterialList
              materials={materials}
              module={module}
              nodeId={selectedNode?.id || 'general'}
              onMaterialAdded={handleMaterialAdded}
              onItemsChange={setItems}
              onOpenMaterial={handleMaterialOpened}
              onDeleteMaterial={handleMaterialDeleted}
            />
          </div>
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={handleClose}>
              {t('ACTIONS.CANCEL')}
            </Button>
            {canShare && (
              <Button prominence="success" key="add-and-share" disabled>
                {t('SHARED_COMPONENTS.ADD_BUNDLE.ACTIONS.ADD_AND_SHARE')}
              </Button>
            )}
            <Button prominence="success" key="add" type="submit" form="form-add-bundle">
              {t('SHARED_COMPONENTS.ADD_BUNDLE.ACTIONS.ADD')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
